import { useMemo, useState } from "react";
import { Info, Loader2 } from "lucide-react";
import { toast } from "sonner";
import type { AxiosError } from "axios";

import { Button } from "@/components/ui/button";
import { apiErrorMessage } from "@/lib/api/error-message";
import { Switch } from "@/components/ui/switch";
import { DraftProvider } from "@/components/itsm-config/draft-context";
import {
  useUpsertIntegrationConfig,
  withProviderConfig,
} from "@/hooks/use-itsm-config";
import { agentSaveError } from "@/lib/itsm-config/agent-rules";
import { jiraInboundSaveError } from "@/lib/itsm-config/general-mapping";
import { mandatoryFieldsSaveError } from "@/lib/itsm-config/mandatory-fields";
import type {
  IntegrationConfigBody,
  ProviderConfig,
} from "@/lib/types/itsm-config";

interface Props {
  tenantId: string;
  credentialId: string;
  provider: string;
  config: IntegrationConfigBody | undefined;
  serverProviderConfig: ProviderConfig;
  /** Renders the card stack against the current draft providerConfig. */
  children: (
    draft: ProviderConfig,
    tags: Record<string, string> | null,
  ) => React.ReactNode;
}

/**
 * Holds the whole tab's edits as an in-memory draft and persists them in a
 * single PUT via the sticky Save bar. Mount this with a `key` on credentialId
 * so switching integrations resets the draft to that integration's server
 * state.
 *
 * Also owns `configuration_customized_by_backoffice`: any config edit flips it
 * true (locking the customer-facing Harmony UI); a toggle lets an admin clear
 * it back to false.
 */
export function ConfigEditor({
  tenantId,
  credentialId,
  provider,
  config,
  serverProviderConfig,
  children,
}: Props) {
  const serverTags = config?.tags ?? null;
  const [draft, setDraft] = useState<ProviderConfig>(serverProviderConfig);
  const [tags, setTagsState] = useState<Record<string, string> | null>(
    serverTags,
  );
  const [customized, setCustomized] = useState<boolean>(
    config?.configuration_customized_by_backoffice ?? false,
  );
  // Last-persisted values we compare against for dirtiness. Advanced to the
  // saved values on a successful PUT — NOT from the refetched prop, whose key
  // order / normalization may differ and would leave the bar stuck.
  const [baseline, setBaseline] =
    useState<ProviderConfig>(serverProviderConfig);
  const [baselineTags, setBaselineTags] = useState<Record<
    string,
    string
  > | null>(serverTags);
  const [baselineCustomized, setBaselineCustomized] = useState<boolean>(
    config?.configuration_customized_by_backoffice ?? false,
  );
  const upsert = useUpsertIntegrationConfig(tenantId, credentialId);

  const dirty = useMemo(
    () =>
      JSON.stringify(draft) !== JSON.stringify(baseline) ||
      JSON.stringify(tags) !== JSON.stringify(baselineTags) ||
      customized !== baselineCustomized,
    [draft, baseline, tags, baselineTags, customized, baselineCustomized],
  );

  // Any config edit marks the integration as backoffice-customized.
  const update: DraftUpdate = (patch) => {
    setDraft((current) => ({
      ...current,
      ...(typeof patch === "function" ? patch(current) : patch),
    }));
    setCustomized(true);
  };
  const applySaved = (
    patch: (current: ProviderConfig) => Partial<ProviderConfig>,
  ) => {
    setDraft((current) => ({ ...current, ...patch(current) }));
    setBaseline((current) => ({ ...current, ...patch(current) }));
  };
  const setTags = (next: Record<string, string> | null) => {
    setTagsState(next);
    setCustomized(true);
  };

  const onSave = () => {
    const invalid = mandatoryFieldsSaveError(draft) ??
      agentSaveError(draft) ??
      jiraInboundSaveError(draft);
    if (invalid) {
      toast.error(invalid);
      return;
    }
    const savedDraft = draft;
    const savedTags = tags;
    const savedCustomized = customized;
    const body: IntegrationConfigBody = {
      ...withProviderConfig(config, provider, savedDraft),
      tags: savedTags,
      configuration_customized_by_backoffice: savedCustomized,
    };
    upsert.mutate(body, {
      onSuccess: () => {
        setBaseline(savedDraft);
        setBaselineTags(savedTags);
        setBaselineCustomized(savedCustomized);
        toast.success("Configuration saved");
      },
      onError: (err) => {
        toast.error(
          apiErrorMessage(
            err as AxiosError<{ detail?: unknown }>,
            "Failed to save",
          ),
        );
      },
    });
  };

  const onDiscard = () => {
    setDraft(baseline);
    setTagsState(baselineTags);
    setCustomized(baselineCustomized);
  };

  return (
    <DraftProvider value={{ update, tags, setTags, applySaved, dirty }}>
      <div className="flex flex-col gap-6 pb-20">
        <CustomizedBanner customized={customized} onChange={setCustomized} />
        {children(draft, tags)}
      </div>

      {dirty && (
        <div className="bg-background/95 fixed inset-x-0 bottom-0 z-20 border-t backdrop-blur">
          <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-6 py-3">
            <span className="text-muted-foreground flex items-center gap-2 text-sm">
              <span className="h-2 w-2 rounded-full bg-amber-500" />
              Unsaved changes
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onDiscard}
                disabled={upsert.isPending}
              >
                Discard
              </Button>
              <Button size="sm" onClick={onSave} disabled={upsert.isPending}>
                {upsert.isPending && (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                )}
                Save changes
              </Button>
            </div>
          </div>
        </div>
      )}
    </DraftProvider>
  );
}

/**
 * Shows whether this integration is customized by backoffice, with a toggle to
 * clear it. When on, the customer-facing Harmony UI locks integration editing.
 */
function CustomizedBanner({
  customized,
  onChange,
}: {
  customized: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div
      className={
        customized
          ? "flex items-start gap-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3"
          : "flex items-start gap-3 rounded-md border px-4 py-3"
      }
    >
      <Info
        className={
          customized
            ? "mt-0.5 h-4 w-4 shrink-0 text-amber-600"
            : "text-muted-foreground mt-0.5 h-4 w-4 shrink-0"
        }
      />
      <div className="flex-1 space-y-0.5">
        <p className="text-sm font-medium">Customized by backoffice</p>
        <p className="text-muted-foreground text-sm">
          {customized
            ? "This integration is managed here. The customer-facing Harmony " +
              "UI is locked for editing and directs users to Harmony support. " +
              "Turn this off to return control to the customer."
            : "Editing any setting below marks this integration as customized " +
              "and locks it in the customer-facing Harmony UI."}
        </p>
      </div>
      <Switch checked={customized} onCheckedChange={onChange} />
    </div>
  );
}

type DraftUpdate = (
  patch:
    | Partial<ProviderConfig>
    | ((current: ProviderConfig) => Partial<ProviderConfig>),
) => void;
